import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import { Link } from 'react-router-dom';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { JournalEntry, LocaleCode } from '@/api/models';
import {
    documentLink,
    documentRefLabel,
    entryNumberDisplay,
    formatEntryDate,
} from '../../journalMeta';
import { ReverseBooksOnlyDialog } from './ReverseBooksOnlyDialog';
import { JournalEntryLines } from '../journalEntryLines';
import { JournalStatusChip } from '../journalStatusChip';
import { useJournalEntryDrawer } from './useJournalEntryDrawer';

type JournalEntryDrawerProps = {
    /** The entry to show; `null` closes the drawer. */
    entryId: string | null;
    locale: LocaleCode;
    onClose: () => void;
    onEdit: (entry: JournalEntry) => void;
    onReversed: () => void;
    /** Follow a reversal link — swaps the drawer to the paired entry. */
    onOpenEntry: (entryId: string) => void;
};

/** The lines table names its own columns — see `JournalEntryLines`. */
const HEADER_LABELS = (t: ReturnType<typeof useTranslation>['t']) => ({
    date: t('journal.drawer.date'),
    document: t('journal.drawer.document'),
    description: t('journal.drawer.description'),
    lines: t('journal.drawer.lines'),
});

/**
 * Right-side detail drawer for a journal entry (HeroUI Pro Sheet). Self-served:
 * fetches the full entry, resolves account names from the chart, and offers the
 * status-appropriate action — Reverse for a posted (immutable) entry, Edit for
 * a draft, nothing for an already-reversed entry.
 */
export const JournalEntryDrawer: React.FC<JournalEntryDrawerProps> = ({
    entryId,
    locale,
    onClose,
    onEdit,
    onReversed,
    onOpenEntry,
}) => {
    const { t } = useTranslation('pages');
    const labels = HEADER_LABELS(t);

    const {
        entry,
        accountMap,
        reversalLink,
        isLoading,
        isReversing,
        actionError,
        onReverse,
        canReverse,
        canForceReverse,
        sourceDocument,
        isConfirmOpen,
        onOpenConfirm,
        onCloseConfirm,
        reason,
        onReasonChange,
        onConfirmForcedReverse,
        canConfirmForcedReverse,
        reverseBlockedReasonKey,
    } = useJournalEntryDrawer({ entryId, locale, onReversed });

    return (
        <Sheet
            isOpen={entryId !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[540px]">
                    <Sheet.Dialog
                        aria-label={entry?.entryNumber ?? labels.lines}
                        {...getNodeTestIdProp('journal-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex items-center gap-3">
                                    <span
                                        className="text-lg font-semibold tabular-nums"
                                        {...getNodeTestIdProp(
                                            'journal-drawer-number',
                                        )}
                                    >
                                        {entry
                                            ? entryNumberDisplay(
                                                  entry,
                                                  t('journal.pendingNumber'),
                                              )
                                            : ''}
                                    </span>
                                    {entry ? (
                                        <JournalStatusChip
                                            slot={`drawer-${entry.id}`}
                                            status={entry.status}
                                        />
                                    ) : null}
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-6">
                            {isLoading ? (
                                <div className="flex flex-col items-center gap-2 py-10 text-muted">
                                    <Icon
                                        aria-hidden
                                        className="size-7 animate-spin text-default-500"
                                        icon="gravity-ui:arrows-rotate-right"
                                    />
                                    <span className="text-sm">
                                        {t('journal.drawer.loading')}
                                    </span>
                                </div>
                            ) : entry ? (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field
                                            label={labels.date}
                                            value={formatEntryDate(entry.date)}
                                        />
                                        {documentLink(
                                            entry.source,
                                            entry.documentRef,
                                        ) ? (
                                            <div>
                                                <p className="text-xs text-muted">
                                                    {labels.document}
                                                </p>
                                                <Link
                                                    className="text-sm text-accent underline-offset-2 hover:underline"
                                                    to={documentLink(
                                                        entry.source,
                                                        entry.documentRef,
                                                    )!}
                                                    {...getNodeTestIdProp(
                                                        'journal-drawer-document-link',
                                                    )}
                                                >
                                                    {documentRefLabel(
                                                        entry.source,
                                                        entry.documentRef,
                                                    )}
                                                </Link>
                                            </div>
                                        ) : (
                                            <Field
                                                label={labels.document}
                                                value={
                                                    documentRefLabel(
                                                        entry.source,
                                                        entry.documentRef,
                                                    ) ?? '–'
                                                }
                                            />
                                        )}
                                        <div className="col-span-2">
                                            <Field
                                                label={labels.description}
                                                value={entry.description || '—'}
                                            />
                                        </div>
                                    </div>

                                    {reversalLink ? (
                                        <button
                                            className="flex w-full items-center justify-between gap-3 rounded-lg bg-surface-secondary px-3 py-2 text-left text-sm text-foreground hover:bg-surface-tertiary"
                                            onClick={() =>
                                                onOpenEntry(reversalLink.id)
                                            }
                                            type="button"
                                            {...getNodeTestIdProp(
                                                'journal-drawer-reversal-link',
                                            )}
                                        >
                                            <span className="flex items-center gap-2">
                                                <Icon
                                                    aria-hidden
                                                    icon="gravity-ui:arrow-rotate-left"
                                                />
                                                {t(
                                                    reversalLink.kind ===
                                                        'reverses'
                                                        ? 'journal.drawer.reversesEntry'
                                                        : 'journal.drawer.reversedByEntry',
                                                    {
                                                        entryNumber:
                                                            reversalLink.entryNumber ??
                                                            t(
                                                                'journal.drawer.linkedEntryLoading',
                                                            ),
                                                    },
                                                )}
                                            </span>
                                            <Icon
                                                aria-hidden
                                                className="shrink-0 text-muted"
                                                icon="gravity-ui:chevron-right"
                                            />
                                        </button>
                                    ) : null}

                                    <div className="flex flex-col gap-2">
                                        <span className="text-xs font-medium uppercase tracking-wide text-muted">
                                            {labels.lines}
                                        </span>
                                        <JournalEntryLines
                                            accountMap={accountMap}
                                            lines={entry.lines}
                                            slot="drawer"
                                            totalCreditTetri={
                                                entry.validationState
                                                    .totalCreditTetri
                                            }
                                            totalDebitTetri={
                                                entry.validationState
                                                    .totalDebitTetri
                                            }
                                        />
                                    </div>

                                    {entry.status === 'POSTED' ? (
                                        <p className="flex items-center gap-2 text-xs text-muted">
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:circle-info"
                                            />
                                            {t('journal.drawer.immutableNote')}
                                        </p>
                                    ) : null}

                                    {actionError ? (
                                        <p
                                            className="text-sm text-danger"
                                            {...getNodeTestIdProp(
                                                'journal-drawer-error',
                                            )}
                                        >
                                            {actionError}
                                        </p>
                                    ) : null}
                                </>
                            ) : (
                                <p className="py-10 text-center text-sm text-muted">
                                    {t('journal.drawer.notFound')}
                                </p>
                            )}
                        </Sheet.Body>

                        <Sheet.Footer>
                            <Button
                                onPress={onClose}
                                variant="tertiary"
                                {...getNodeTestIdProp('journal-drawer-close')}
                            >
                                {t('journal.drawer.close')}
                            </Button>
                            {canReverse ? (
                                <Button
                                    isPending={isReversing}
                                    onPress={onReverse}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'journal-drawer-reverse',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:arrow-rotate-left"
                                    />
                                    {t('journal.drawer.reverse')}
                                </Button>
                            ) : null}
                            {canForceReverse ? (
                                <>
                                    {sourceDocument?.href ? (
                                        <Link
                                            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-foreground hover:opacity-90"
                                            to={sourceDocument.href}
                                            {...getNodeTestIdProp(
                                                'journal-drawer-go-to-source',
                                            )}
                                        >
                                            {t(sourceDocument.labelKey)}
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:arrow-up-right-from-square"
                                            />
                                        </Link>
                                    ) : null}
                                    <Button
                                        onPress={onOpenConfirm}
                                        variant="danger"
                                        {...getNodeTestIdProp(
                                            'journal-drawer-reverse-books-only',
                                        )}
                                    >
                                        {t('journal.drawer.reverseBooksOnly')}
                                    </Button>
                                </>
                            ) : null}
                            {reverseBlockedReasonKey ? (
                                <p
                                    className="max-w-sm text-xs text-muted"
                                    {...getNodeTestIdProp(
                                        'journal-drawer-reverse-blocked',
                                    )}
                                >
                                    {t(reverseBlockedReasonKey)}
                                </p>
                            ) : null}
                            {entry?.status === 'DRAFT' ? (
                                <Button
                                    onPress={() => onEdit(entry)}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'journal-drawer-edit',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:pencil"
                                    />
                                    {t('journal.drawer.edit')}
                                </Button>
                            ) : null}
                        </Sheet.Footer>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
            <ReverseBooksOnlyDialog
                canConfirm={canConfirmForcedReverse}
                isOpen={isConfirmOpen}
                isPending={isReversing}
                onClose={onCloseConfirm}
                onConfirm={onConfirmForcedReverse}
                onReasonChange={onReasonChange}
                reason={reason}
                sourceLabelKey={sourceDocument?.sourceLabelKey ?? null}
            />
        </Sheet>
    );
};

const Field: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span className="text-sm text-foreground">{value}</span>
    </div>
);
